import { useState, useMemo } from 'react';
import { 
  Brain, Calendar, Clock, Briefcase, TrendingUp, Home, 
  AlertCircle, Sparkles, ArrowLeft, ArrowRight, Check, 
  ChevronRight, Info, Shield, RefreshCw, HeartPulse, 
  CircleDollarSign, User, Coins, CheckCircle2, Copy, FileText,
  ChevronDown, Trash2, Zap, RotateCcw
} from 'lucide-react';
import { format, addMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';
import { getMonthlyData } from '../features/statistics/statisticsLogic';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const fmtShort = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '€0';
  if (Math.abs(n) >= 1_000_000) return `€${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `€${(n / 1_000).toFixed(0)}k`;
  return `€${n.toFixed(0)}`;
};

export default function InvestmentAIAdvisor({ 
  userSettings = {}, 
  onUpdateSettings, 
  wallets = [], 
  expenses = [], 
  incomes = [],
  debts = [],
  addDocument,
  pacAttuale = 0,
  liquidValue = 0,
  depositValue = 0,
  etfValue = 0,
  azioniValue = 0,
  obbligazioniValue = 0,
  avgExpectedEtf = 8,
  avgExpectedAzioni = 6,
  avgExpectedObbl = 4,
  avgExpectedDeposit = 0,
}) {
  const profile = userSettings.investmentProfile || null;
  const aiPlan = userSettings.aiInvestmentPlan || null;
  const [isEditing, setIsEditing] = useState(!profile);
  const [currentStep, setCurrentStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [parseError, setParseError] = useState(null);
  const [showCopySection, setShowCopySection] = useState(!aiPlan);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  // Form states
  const [birthDate, setBirthDate] = useState(profile?.birthDate || '');
  const [timeHorizon, setTimeHorizon] = useState(profile?.timeHorizon || '');
  const [workType, setWorkType] = useState(profile?.workType || '');
  const [incomeStability, setIncomeStability] = useState(profile?.incomeStability || 3);
  const [tfrManagement, setTfrManagement] = useState(profile?.tfrManagement || '');
  const [realEstate, setRealEstate] = useState(profile?.realEstate || '');
  const [debtsCredits, setDebtsCredits] = useState(profile?.debtsCredits || '');
  const [psychologicalProfile, setPsychologicalProfile] = useState(profile?.psychologicalProfile || '');
  const [specialSituations, setSpecialSituations] = useState(profile?.specialSituations || '');

  // Calculate age from birthdate
  const age = useMemo(() => {
    if (!birthDate) return null;
    const today = new Date();
    const birthDateObj = new Date(birthDate);
    let calculatedAge = today.getFullYear() - birthDateObj.getFullYear();
    const m = today.getMonth() - birthDateObj.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDateObj.getDate())) {
      calculatedAge--;
    }
    return calculatedAge;
  }, [birthDate]);

  // Recommended Horizon based on age
  const recommendedHorizon = useMemo(() => {
    if (age === null) return null;
    if (age < 50) return '10+';
    if (age >= 50 && age < 65) return '5-10';
    return '3-5';
  }, [age]);

  const handleBirthDateChange = (e) => {
    const val = e.target.value;
    setBirthDate(val);
    if (!timeHorizon) {
      const bDate = new Date(val);
      const today = new Date();
      let calculatedAge = today.getFullYear() - bDate.getFullYear();
      const m = today.getMonth() - bDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < bDate.getDate())) {
        calculatedAge--;
      }
      if (calculatedAge < 50) setTimeHorizon('10+');
      else if (calculatedAge >= 50 && calculatedAge < 65) setTimeHorizon('5-10');
      else setTimeHorizon('3-5');
    }
  };

  const handleNext = () => {
    if (currentStep < 5) setCurrentStep(s => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(s => s - 1);
  };

  const handleSave = async () => {
    if (!birthDate || !timeHorizon || !workType.trim()) {
      alert('Per favore, compila la data di nascita, l\'orizzonte temporale e il tipo di lavoro prima di salvare.');
      return;
    }

    const newProfile = {
      birthDate,
      timeHorizon,
      workType,
      incomeStability: Number(incomeStability),
      tfrManagement,
      realEstate,
      debtsCredits,
      psychologicalProfile,
      specialSituations,
      completedAt: new Date().toISOString()
    };

    try {
      await onUpdateSettings({
        ...userSettings,
        investmentProfile: newProfile
      });
      setIsEditing(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('Errore durante il salvataggio del profilo di investimento:', error);
      alert('Si è verificato un errore durante il salvataggio. Riprova.');
    }
  };

  const classifyWalletType = (wallet) => {
    const t = (wallet.type || '').toLowerCase();
    if (t.includes('deposito')) return 'deposito';
    if (t.includes('etf')) return 'etf';
    if (t.includes('azione') || t.includes('azioni')) return 'azioni';
    if (t.includes('obbligazione') || t.includes('obbligazioni')) return 'obbligazioni';
    return null;
  };

  // Use dashboard-consistent values (same as Panoramica: deposito=current, etf/azioni/obbl=prev-month)
  const actualValues = useMemo(() => ({
    etf: etfValue,
    azioni: azioniValue,
    obbligazioni: obbligazioniValue,
    deposito: depositValue,
  }), [etfValue, azioniValue, obbligazioniValue, depositValue]);

  const totalInvested = etfValue + azioniValue + obbligazioniValue + depositValue;

  const actualPcts = useMemo(() => {
    if (totalInvested === 0) return { etf: 0, azioni: 0, obbligazioni: 0, deposito: 0 };
    return {
      etf: (etfValue / totalInvested) * 100,
      azioni: (azioniValue / totalInvested) * 100,
      obbligazioni: (obbligazioniValue / totalInvested) * 100,
      deposito: (depositValue / totalInvested) * 100,
    };
  }, [etfValue, azioniValue, obbligazioniValue, depositValue, totalInvested]);

  // Liquidità totale (liquida + deposito)
  const totalLiquidity = liquidValue + depositValue;

  // Labels helper
  const stabilityLabel = (stability) => {
    const labels = [
      'Altamente volatile (es. freelancer all\'inizio o stagionale)',
      'Volatile (es. P.IVA avviata)',
      'Moderatamente stabile (es. contratto a tempo determinato)',
      'Stabile (es. dipendente privato indeterminato)',
      'Molto stabile (es. dipendente pubblico o pensionato)'
    ];
    return labels[stability - 1] || 'Non specificato';
  };

  const horizonLabel = (horizon) => {
    if (horizon === '3-5') return 'Breve termine (3-5 anni)';
    if (horizon === '5-10') return 'Medio termine (5-10 anni)';
    if (horizon === '10+') return 'Lungo termine (10+ anni)';
    return 'Non specificato';
  };

  // Detailed wallet summary for prompt
  const walletsSummaryText = useMemo(() => {
    return wallets
      .map(w => {
        const type = classifyWalletType(w);
        const balStr = formatCurrency(w.balance || 0);
        const monthlyPac = parseFloat(w.monthlyContribution) || 0;
        const pacStr = monthlyPac > 0 ? ` | PAC: ${formatCurrency(monthlyPac)}/mese` : '';
        
        if (type === 'deposito') {
          const rate = w.grossAnnualRate ? `${w.grossAnnualRate}% lordo (${(w.grossAnnualRate * 0.74).toFixed(2)}% netto)` : 'tasso non specificato';
          return `- ${w.name} (Conto Deposito): ${balStr} | Tasso: ${rate}`;
        } else if (type === 'etf') {
          const isin = w.isinCode ? ` [${w.isinCode}]` : '';
          const isinName = w.isinName ? ` - ${w.isinName}` : '';
          const rate = w.expectedRate ? ` | Rendimento atteso: ${w.expectedRate}%/a` : '';
          return `- ${w.name}${isin}${isinName} (ETF): ${balStr}${rate}${pacStr}`;
        } else if (type === 'azioni') {
          const isin = w.isinCode ? ` [${w.isinCode}]` : '';
          const isinName = w.isinName ? ` - ${w.isinName}` : '';
          return `- ${w.name}${isin}${isinName} (Azioni): ${balStr}${pacStr}`;
        } else if (type === 'obbligazioni') {
          const isin = w.isinCode ? ` [${w.isinCode}]` : '';
          const isinName = w.isinName ? ` - ${w.isinName}` : '';
          return `- ${w.name}${isin}${isinName} (Obbligazioni): ${balStr}${pacStr}`;
        } else {
          return `- ${w.name} (${w.type || 'Liquidità'}): ${balStr}`;
        }
      })
      .join('\n');
  }, [wallets]);

  const debtsSummary = useMemo(() => {
    const personBalances = {};
    if (Array.isArray(debts)) {
      debts.forEach((debt) => {
        const personName = debt?.personName;
        const amount = parseFloat(debt?.amount) || 0;
        if (!personName) return;
        const delta = debt.type === 'credit' ? amount : -amount;
        personBalances[personName] = (personBalances[personName] || 0) + delta;
      });
    }
    const creditTotal = Object.values(personBalances).filter(v => v > 0.01).reduce((sum, v) => sum + v, 0);
    const debtTotal = Object.values(personBalances).filter(v => v < -0.01).reduce((sum, v) => sum + Math.abs(v), 0);
    return { creditTotal, debtTotal };
  }, [debts]);

  const netWorthVal = useMemo(() => {
    const totalAssets = wallets.reduce((sum, w) => sum + (parseFloat(w.balance) || 0), 0);
    return totalAssets + debtsSummary.creditTotal - debtsSummary.debtTotal;
  }, [wallets, debtsSummary]);

  const past6MonthsData = useMemo(() => {
    const now = new Date();
    const currentMonthKey = format(now, 'yyyy-MM');
    const allMonthlyData = getMonthlyData(expenses, incomes);
    return allMonthlyData
      .filter(m => m.month < currentMonthKey)
      .sort((a, b) => b.month.localeCompare(a.month))
      .slice(0, 6)
      .reverse();
  }, [expenses, incomes]);

  // Top Categories & Stores for Prompt Context
  const topFinancialInfo = useMemo(() => {
    const activeExpenses = expenses.filter(e => e && e.category !== 'Trasferimento');
    const activeIncomes = incomes.filter(i => i && i.category !== 'Trasferimento');

    const expenseCatMap = {};
    activeExpenses.forEach(e => {
      const cat = e.category || 'Altro';
      expenseCatMap[cat] = (expenseCatMap[cat] || 0) + (parseFloat(e.amount) || 0);
    });
    const sortedExpenseCats = Object.entries(expenseCatMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const expenseStoreMap = {};
    activeExpenses.forEach(e => {
      const store = e.store || 'Altro/Non specificato';
      expenseStoreMap[store] = (expenseStoreMap[store] || 0) + (parseFloat(e.amount) || 0);
    });
    const sortedExpenseStores = Object.entries(expenseStoreMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const incomeCatMap = {};
    activeIncomes.forEach(i => {
      const cat = i.category || 'Altro';
      incomeCatMap[cat] = (incomeCatMap[cat] || 0) + (parseFloat(i.amount) || 0);
    });
    const sortedIncomeCats = Object.entries(incomeCatMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

    return { expenseCats: sortedExpenseCats, expenseStores: sortedExpenseStores, incomeCats: sortedIncomeCats };
  }, [expenses, incomes]);

  const aiPromptText = useMemo(() => {
    const ageVal = age !== null ? `${age} anni` : 'Non specificata';
    const totalAssets = wallets.reduce((sum, w) => sum + (parseFloat(w.balance) || 0), 0);

    const expenseCatsText = topFinancialInfo.expenseCats
      .map(([cat, val]) => `  • ${cat}: ${formatCurrency(val)}`).join('\n');
    const expenseStoresText = topFinancialInfo.expenseStores
      .map(([store, val]) => `  • ${store}: ${formatCurrency(val)}`).join('\n');
    const incomeCatsText = topFinancialInfo.incomeCats
      .map(([cat, val]) => `  • ${cat}: ${formatCurrency(val)}`).join('\n');

    const avgSavingsMonthly = past6MonthsData.length > 0
      ? past6MonthsData.reduce((s, m) => s + ((parseFloat(m.Entrate) || 0) - (parseFloat(m.Spese) || 0)), 0) / past6MonthsData.length
      : 0;

    // Wallets con gain potenziale (etf/azioni)
    const investmentWallets = wallets.filter(w => {
      const t = classifyWalletType(w);
      return t === 'etf' || t === 'azioni' || t === 'obbligazioni';
    });

    return `Sei un consulente finanziario professionista di altissimo livello (IA). Analizza ATTENTAMENTE la mia situazione finanziaria e gli obiettivi, e genera un piano finanziario personalizzato DETTAGLIATO E PROFESSIONALE in formato JSON.

IMPORTANTE: Comportati come un consulente finanziario esperto e lungimirante. Non dare consigli generici: motiva OGNI scelta in modo dettagliato come faresti con un cliente di alto profilo. Considera sempre le implicazioni fiscali (capital gain al 26% in Italia) quando suggerisci la vendita di asset con plusvalenza.

### DATI FINANZIARI ATTUALI
- Patrimonio Netto Totale: ${formatCurrency(netWorthVal)}
- Totale Liquidità e Investimenti: ${formatCurrency(totalAssets)}
Dettaglio completo dei conti:
${walletsSummaryText}
${debtsSummary.creditTotal > 0.01 ? `- Crediti verso terzi: ${formatCurrency(debtsSummary.creditTotal)}\n` : ''}${debtsSummary.debtTotal > 0.01 ? `- Debiti verso terzi: ${formatCurrency(debtsSummary.debtTotal)}\n` : ''}
### STORICO ENTRATE E USCITE (Ultimi ${past6MonthsData.length} mesi conclusi)
${past6MonthsData.map(m => {
  const [year, month] = m.month.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  const monthName = format(date, 'MMMM yyyy', { locale: it });
  const inc = parseFloat(m.Entrate) || 0;
  const exp = parseFloat(m.Spese) || 0;
  const sav = inc - exp;
  return `- ${monthName}: Entrate: ${formatCurrency(inc)}, Uscite: ${formatCurrency(exp)}, Risparmio: ${formatCurrency(sav)}`;
}).join('\n')}
- Risparmio medio mensile (ultimi 6 mesi): ${formatCurrency(avgSavingsMonthly)}
- PAC mensile attuale (vers. in titoli): ${formatCurrency(pacAttuale)}

### MAGGIORI ENTRATE E USCITE PER CATEGORIA / NEGOZIO
- Maggiori Categorie di Spesa:
${expenseCatsText || '  • Nessun dato'}
- Maggiori Negozi di Spesa:
${expenseStoresText || '  • Nessun dato'}
- Maggiori Categorie di Entrate:
${incomeCatsText || '  • Nessun dato'}

### PROFILATURA UTENTE
1. Data di Nascita: ${birthDate} (Età: ${ageVal})
2. Orizzonte Temporale: ${horizonLabel(timeHorizon)}
3. Situazione lavorativa: ${workType}
4. Stabilità del reddito: ${incomeStability}/5 (${stabilityLabel(incomeStability)})
5. Gestione TFR: ${tfrManagement || 'Non specificata'}
6. Immobili / Obiettivo Casa: ${realEstate || 'Non specificato'}
7. Debiti / Crediti in corso (dettagli): ${debtsCredits || 'Nessuno'}
8. Situazioni particolari / Spese imminenti: ${specialSituations || 'Nessuna'}
9. Reazione psicologica a crollo del mercato (-30%): ${psychologicalProfile}

### RICHIESTA
Fornisci una risposta contenente ESCLUSIVAMENTE un blocco di codice JSON valido. Il JSON deve avere la struttura definita sotto.

LINEE GUIDA OPERATIVE PER L'ACTION PLAN (campo "actionPlan"):
1. Per ogni asset ETF/Azioni/Obbligazioni esistente: valuta se il titolo è ottimale o dovrebbe essere sostituito. Considera che la vendita comporta il pagamento del 26% di tasse sulle plusvalenze (capital gain). Calcola se conviene vendere o mantenere.
2. NON consigliare mai "Azioni" generiche: se consigli azioni, specifica esattamente ticker/ISIN e motivo.
3. Per ETF: consiglia strumenti specifici (es. MSCI World come IE00B4L5Y983, S&P 500 come IE00B3XXRP09, ecc.) con motivazione.
4. Specifica esattamente dove allocare il capitale liquido immediatamente disponibile (dopo fondo emergenza).
5. Il piano deve essere actionable: ogni azione deve essere chiara, precisa e immediatamente eseguibile.

LINEE GUIDA PER LA SIMULAZIONE:
- "projYears": numero di anni (in base all'orizzonte temporale e all'età)
- "immediateChange": importo da spostare SUBITO in questa asset class (positivo = acquisto, negativo = vendita/disinvestimento)
- "amount" nelle "phases": importo mensile PAC per questa asset class

Il JSON DEVE avere ESCLUSIVAMENTE questa struttura:
{
  "planName": "<nome premium e motivante, es: 'Strategia di Crescita Aggressiva a Lungo Termine'>",
  "rationale": "<spiegazione strategica dettagliata (almeno 300 parole): motivo dell'asset allocation scelta, perché questi specifici ETF/strumenti, analisi dell'attuale portafoglio, cosa cambiare e perché, considerazioni fiscali sul capital gain, obiettivi a lungo termine>",
  "recommendedEmergencyFund": <valore numerico in euro>,
  "recommendedPac": <valore numerico totale PAC mensile consigliato in euro>,
  "recommendedAllocation": {
    "etf": <percentuale target 0-100>,
    "azioni": <percentuale target 0-100, usare 0 se non si consigliano azioni specifiche>,
    "obbligazioni": <percentuale target 0-100>,
    "deposito": <percentuale target 0-100>
  },
  "actionPlan": {
    "immediateActions": [
      {
        "action": "vendi|mantieni|acquista|trasferisci",
        "asset": "<nome preciso del titolo o conto>",
        "isin": "<ISIN se applicabile, altrimenti null>",
        "amount": <importo in euro, 0 se non definito>,
        "capitalGainNote": "<nota sulle tasse da pagare se si vende, altrimenti null>",
        "reason": "<motivazione professionale e dettagliata di questa specifica azione>"
      }
    ],
    "pacInstructions": "<istruzioni precise per il PAC: dove versare mensilmente, quanto, su quali titoli specifici con ISIN>",
    "depositoInstructions": "<cosa fare con i conti deposito esistenti: mantenere, ridurre, quale tasso minimo accettare>",
    "notes": "<note aggiuntive importanti per il cliente>"
  },
  "simulationTimeline": {
    "projYears": <numero>,
    "etfConfig": {
      "immediateChange": <numero>,
      "phases": [{ "amount": <numero>, "endYear": <numero_o_null> }]
    },
    "azioniConfig": {
      "immediateChange": <numero>,
      "phases": [{ "amount": <numero>, "endYear": <numero_o_null> }]
    },
    "obblConfig": {
      "immediateChange": <numero>,
      "phases": [{ "amount": <numero>, "endYear": <numero_o_null> }]
    },
    "depositoConfig": {
      "immediateChange": <numero>,
      "phases": [{ "amount": <numero>, "endYear": <numero_o_null> }]
    }
  }
}

Assicurati che la somma di recommendedAllocation.etf + azioni + obbligazioni + deposito = 100.
Non includere testo fuori dal JSON. Rispondi SOLO con il blocco JSON in markdown.`;
  }, [wallets, age, birthDate, timeHorizon, workType, incomeStability, tfrManagement, realEstate, debtsCredits, specialSituations, psychologicalProfile, walletsSummaryText, debtsSummary, netWorthVal, past6MonthsData, topFinancialInfo, pacAttuale]);

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(aiPromptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleImportJSON = async () => {
    setParseError(null);
    let raw = jsonText.trim();
    if (!raw) { setParseError('Incolla il JSON ricevuto prima di importare.'); return; }

    const match = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (match) raw = match[1].trim();

    try {
      const parsed = JSON.parse(raw);

      if (!parsed.planName || !parsed.rationale || !parsed.recommendedAllocation) {
        throw new Error("Il JSON deve contenere 'planName', 'rationale' e 'recommendedAllocation'.");
      }

      const alloc = parsed.recommendedAllocation;
      const totalAlloc = (Number(alloc.etf) || 0) + (Number(alloc.azioni) || 0) + (Number(alloc.obbligazioni) || 0) + (Number(alloc.deposito) || 0);
      if (Math.abs(totalAlloc - 100) > 0.1) {
        alert(`Nota: la somma delle percentuali è ${totalAlloc}%, dovrebbe essere 100%. Il piano verrà comunque salvato.`);
      }

      if (parsed.simulationTimeline) {
        const timeline = parsed.simulationTimeline;
        const currentYear = new Date().getFullYear();
        const mapConfig = (cfg) => {
          if (!cfg) return { immediateChange: 0, phases: [{ amount: 0, endYear: 2100 }] };
          const phases = (cfg.phases || []).map(p => {
            let ey = (p.endYear === null || p.endYear === undefined) ? 2100 : Number(p.endYear);
            if (!isNaN(ey) && ey < 1000) {
              ey = currentYear + ey;
            }
            return {
              amount: Number(p.amount) || 0,
              endYear: ey
            };
          });
          if (phases.length === 0) {
            phases.push({ amount: 0, endYear: 2100 });
          }
          return {
            immediateChange: Number(cfg.immediateChange) || 0,
            phases
          };
        };

        const newPlan = {
          name: `${parsed.planName} (AI)`,
          projYears: Number(timeline.projYears) || 10,
          etfConfig: mapConfig(timeline.etfConfig || timeline.etf),
          azioniConfig: mapConfig(timeline.azioniConfig || timeline.azioni || timeline.azioneConfig || timeline.azione),
          obblConfig: mapConfig(timeline.obblConfig || timeline.obbligazioniConfig || timeline.obbConfig || timeline.obbligazioni),
          depositoConfig: mapConfig(timeline.depositoConfig || timeline.depositConfig || timeline.deposito || timeline.deposit),
          createdAt: new Date().toISOString(),
          isAIPlan: true
        };
        if (addDocument) {
          await addDocument('investmentPlans', newPlan);
        }

        parsed.simulationTimeline = {
          projYears: Number(timeline.projYears) || 10,
          etfConfig: newPlan.etfConfig,
          azioniConfig: newPlan.azioniConfig,
          obblConfig: newPlan.obblConfig,
          depositoConfig: newPlan.depositoConfig
        };
      }

      await onUpdateSettings({ ...userSettings, aiInvestmentPlan: parsed });

      setJsonText('');
      setShowCopySection(false);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 4000);
    } catch (err) {
      console.error(err);
      setParseError(`Errore nel parsing del JSON: ${err.message}. Controlla la sintassi.`);
    }
  };

  const handleCancelPlan = async () => {
    try {
      await onUpdateSettings({ ...userSettings, aiInvestmentPlan: null });
      setShowCopySection(true);
    } catch (err) {
      console.error(err);
      alert('Errore durante la rimozione del piano AI.');
    }
  };

  const rebalancingAdvice = useMemo(() => {
    if (!aiPlan) return null;
    const target = aiPlan.recommendedAllocation;
    return [
      { name: 'ETF', actual: actualPcts.etf, target: Number(target.etf) || 0, color: '#eab308' },
      { name: 'Azioni', actual: actualPcts.azioni, target: Number(target.azioni) || 0, color: '#6366f1' },
      { name: 'Obbligazioni', actual: actualPcts.obbligazioni, target: Number(target.obbligazioni) || 0, color: '#f97316' },
      { name: 'Deposito', actual: actualPcts.deposito, target: Number(target.deposito) || 0, color: '#10b981' }
    ];
  }, [aiPlan, actualPcts]);

  // Embedded timeline simulation
  const embeddedTimelineData = useMemo(() => {
    if (!aiPlan?.simulationTimeline) return [];
    const tl = aiPlan.simulationTimeline;
    const projYears = Number(tl.projYears) || 10;
    const baseYear = new Date().getFullYear();

    // getPhase: supports both relative years (e.g. 5 = 5 years from now)
    // and absolute calendar years (e.g. 2031). Values >= 1000 are treated as calendar years.
    const getPhase = (config, yr) => {
      if (!config?.phases) return 0;
      for (const phase of config.phases) {
        const amt = Number(phase.amount) || 0;
        const endYr = (phase.endYear === null || phase.endYear === undefined) ? 2100 : Number(phase.endYear);
        // If endYear looks like a calendar year (e.g. 2030), convert to relative years
        const duration = endYr >= 1000 ? endYr - baseYear : endYr;
        if (duration <= 0) continue;
        if (yr <= duration) return amt;
      }
      return 0;
    };

    let b_dep = actualValues.deposito + (Number(tl.depositoConfig?.immediateChange) || 0);
    let b_etf = actualValues.etf + (Number(tl.etfConfig?.immediateChange) || 0);
    let b_azi = actualValues.azioni + (Number(tl.azioniConfig?.immediateChange) || 0);
    let b_obb = actualValues.obbligazioni + (Number(tl.obblConfig?.immediateChange) || 0);

    const data = [{ year: 'Oggi', total: Math.round(liquidValue + b_dep + b_etf + b_azi + b_obb), capitale: Math.round(liquidValue + b_dep + b_etf + b_azi + b_obb), interessi: 0 }];

    let capitalBase = liquidValue + b_dep + b_etf + b_azi + b_obb;
    let accInt = 0;

    for (let yr = 1; yr <= projYears; yr++) {
      const add_dep = getPhase(tl.depositoConfig, yr);
      const add_etf = getPhase(tl.etfConfig, yr);
      const add_azi = getPhase(tl.azioniConfig, yr);
      const add_obb = getPhase(tl.obblConfig, yr);

      for (let m = 1; m <= 12; m++) {
        const int_dep = b_dep * (avgExpectedDeposit / 100 / 12);
        const int_etf = b_etf * (avgExpectedEtf / 100 / 12);
        const int_azi = b_azi * (avgExpectedAzioni / 100 / 12);
        const int_obb = b_obb * (avgExpectedObbl / 100 / 12);
        accInt += int_dep + int_etf + int_azi + int_obb;
        b_dep += int_dep + add_dep;
        b_etf += int_etf + add_etf;
        b_azi += int_azi + add_azi;
        b_obb += int_obb + add_obb;
        capitalBase += add_dep + add_etf + add_azi + add_obb;
      }

      data.push({
        year: String(baseYear + yr),
        capitale: Math.round(capitalBase),
        interessi: Math.round(accInt),
        total: Math.round(capitalBase + accInt)
      });
    }
    return data;
  }, [aiPlan, etfValue, azioniValue, obbligazioniValue, depositValue, liquidValue, avgExpectedDeposit, avgExpectedEtf, avgExpectedAzioni, avgExpectedObbl]);

  const finalTimelineValue = embeddedTimelineData.length > 0 ? embeddedTimelineData[embeddedTimelineData.length - 1] : null;

  return (
    <div className="space-y-6">
      {/* ────────────────── QUESTIONNAIRE WIZARD ────────────────── */}
      {isEditing ? (
        <div className="card p-6 border-violet-500/20 dark:border-violet-500/30 shadow-violet-500/5 dark:shadow-violet-500/2 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/5 dark:bg-violet-500/10 rounded-full blur-2xl pointer-events-none" />
          
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 flex items-center justify-center shadow-sm">
              <Brain className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-900 dark:text-gray-100">Profilazione AI degli Investimenti</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Rispondi a poche domande per generare la tua strategia personalizzata</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center text-xs font-bold text-gray-400 mb-2">
              <span>STEP {currentStep} DI 5</span>
              <span>{Math.round((currentStep / 5) * 100)}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 dark:bg-gray-700/50 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full transition-all duration-500" style={{ width: `${(currentStep / 5) * 100}%` }} />
            </div>
          </div>

          {/* Step Contents */}
          <div className="min-h-[220px] transition-all duration-300">
            {/* STEP 1 */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-fade-in-up">
                <h4 className="text-sm font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-violet-500" /> Dati Personali & Orizzonte
                </h4>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300" htmlFor="birthDate">Data di nascita *</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="date" id="birthDate" value={birthDate} onChange={handleBirthDateChange} className="input pl-11" required />
                  </div>
                  {age !== null && <div className="text-[11px] font-bold text-violet-600 dark:text-violet-400 mt-1">Età rilevata: {age} anni</div>}
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 flex justify-between">
                    <span>Orizzonte temporale desiderato *</span>
                    {recommendedHorizon && <span className="text-violet-600 dark:text-violet-400 font-extrabold text-[10px]">Consigliato: {horizonLabel(recommendedHorizon)}</span>}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ val: '3-5', label: '3-5 anni' }, { val: '5-10', label: '5-10 anni' }, { val: '10+', label: '10+ anni' }].map(opt => {
                      const isSelected = opt.val === timeHorizon;
                      return (
                        <button key={opt.val} type="button" onClick={() => setTimeHorizon(opt.val)}
                          className={`p-3 rounded-xl border font-bold text-xs flex flex-col items-center justify-center gap-1.5 transition-all ${isSelected ? 'border-violet-500 bg-violet-500/10 text-violet-700 dark:text-violet-400 ring-2 ring-violet-500/30' : 'border-gray-200 dark:border-gray-700 bg-transparent hover:border-violet-300'}`}>
                          <span>{opt.label}</span>
                          {opt.val === recommendedHorizon && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 font-extrabold uppercase">Cons.</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {currentStep === 2 && (
              <div className="space-y-4 animate-fade-in-up">
                <h4 className="text-sm font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-violet-500" /> Situazione lavorativa
                </h4>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300" htmlFor="workType">Che tipo di lavoro hai? *</label>
                  <p className="text-[10px] text-gray-400 leading-tight mb-1.5">(es. contratto a tempo indeterminato, determinato, P.IVA, studente, pensionato, ecc. Argomenta specificando stabilità e prospettive)</p>
                  <textarea id="workType" value={workType} onChange={(e) => setWorkType(e.target.value)} className="input h-24 py-2 resize-none placeholder:text-xs" placeholder="Descrivi la tua attività lavorativa e la natura del tuo reddito..." required />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 flex justify-between" htmlFor="incomeStability">
                    <span>Stabilità del reddito ({incomeStability}/5)</span>
                    <span className="text-violet-600 dark:text-violet-400 font-black text-xs">
                      {incomeStability === 1 && 'Altamente Volatile'}{incomeStability === 2 && 'Volatile'}{incomeStability === 3 && 'Moderata'}{incomeStability === 4 && 'Stabile'}{incomeStability === 5 && 'Molto Stabile'}
                    </span>
                  </label>
                  <input type="range" id="incomeStability" min="1" max="5" value={incomeStability} onChange={(e) => setIncomeStability(Number(e.target.value))} className="w-full accent-violet-600 dark:accent-violet-500 cursor-pointer h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none" />
                  <div className="flex justify-between text-[9px] font-bold text-gray-400 px-1">
                    <span>MOLTO VOLATILE</span><span>STABILE</span><span>MASSIMA STABILITÀ</span>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {currentStep === 3 && (
              <div className="space-y-4 animate-fade-in-up">
                <h4 className="text-sm font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                  <Home className="w-4 h-4 text-violet-500" /> Casa & Previdenza
                </h4>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300" htmlFor="realEstate">Possiedi immobili o intendi acquistare casa?</label>
                  <p className="text-[10px] text-gray-400 leading-tight mb-1.5">(se hai immobili, prevedi di comprare casa a breve, se vivi in affitto o con i genitori)</p>
                  <textarea id="realEstate" value={realEstate} onChange={(e) => setRealEstate(e.target.value)} className="input h-20 py-2 resize-none placeholder:text-xs" placeholder="Descrivi la tua situazione abitativa e obiettivi immobiliari..." />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300" htmlFor="tfrManagement">Gestione del TFR</label>
                  <p className="text-[10px] text-gray-400 leading-tight mb-1.5">(Se sei dipendente: dove si trova il TFR — in azienda, fondo pensione negoziale, fondo aperto?)</p>
                  <textarea id="tfrManagement" value={tfrManagement} onChange={(e) => setTfrManagement(e.target.value)} className="input h-20 py-2 resize-none placeholder:text-xs" placeholder="Dettagli sulla gestione del TFR o fondi pensione integrativi..." />
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {currentStep === 4 && (
              <div className="space-y-4 animate-fade-in-up">
                <h4 className="text-sm font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                  <CircleDollarSign className="w-4 h-4 text-violet-500" /> Finanze & Debiti
                </h4>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300" htmlFor="debtsCredits">Hai debiti o crediti in corso?</label>
                  <p className="text-[10px] text-gray-400 leading-tight mb-1.5">(Specifica natura: mutuo, prestito auto, rateizzazioni. Includi durata residua stimata)</p>
                  <textarea id="debtsCredits" value={debtsCredits} onChange={(e) => setDebtsCredits(e.target.value)} className="input h-20 py-2 resize-none placeholder:text-xs" placeholder="Dettaglia i debiti attivi o eventuali crediti di rilievo..." />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300" htmlFor="specialSituations">Situazioni particolari o grandi spese imminenti?</label>
                  <p className="text-[10px] text-gray-400 leading-tight mb-1.5">(matrimonio, acquisto auto, viaggio significativo, corsi di studi costosi, ecc.)</p>
                  <textarea id="specialSituations" value={specialSituations} onChange={(e) => setSpecialSituations(e.target.value)} className="input h-20 py-2 resize-none placeholder:text-xs" placeholder="Descrivi costi o traguardi finanziari pianificati a breve termine..." />
                </div>
              </div>
            )}

            {/* STEP 5 */}
            {currentStep === 5 && (
              <div className="space-y-4 animate-fade-in-up">
                <h4 className="text-sm font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                  <HeartPulse className="w-4 h-4 text-violet-500" /> Profilo Psicologico
                </h4>
                <div className="p-3.5 rounded-2xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/30 flex gap-3 text-left">
                  <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-normal font-medium">
                    <strong>Pillola Finanziaria:</strong> La statistica mostra che la scelta migliore durante un crollo di mercato è mantenere i propri investimenti, ignorando la volatilità temporanea. Tuttavia, investire oltre la propria tolleranza al rischio porta spesso a vendite in perdita dettate dal panico.
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300" htmlFor="psychologicalProfile">Cosa faresti se i tuoi investimenti crollassero del 30% all'improvviso? *</label>
                  <p className="text-[10px] text-gray-400 leading-tight mb-1.5">(Venderesti tutto? Manterresti? Compresti di più a prezzi scontati? Sii sincero)</p>
                  <textarea id="psychologicalProfile" value={psychologicalProfile} onChange={(e) => setPsychologicalProfile(e.target.value)} className="input h-24 py-2 resize-none placeholder:text-xs" placeholder="Esprimi la tua reazione psicologica a forti crolli di mercato..." required />
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100 dark:border-gray-700/50">
            <button type="button" onClick={handleBack} disabled={currentStep === 1} className="btn btn-secondary h-10 px-4 py-2 flex items-center gap-1.5 disabled:opacity-30 disabled:pointer-events-none">
              <ArrowLeft className="w-4 h-4" /> Indietro
            </button>
            {currentStep < 5 ? (
              <button type="button" onClick={handleNext}
                disabled={(currentStep === 1 && (!birthDate || !timeHorizon)) || (currentStep === 2 && !workType.trim())}
                className="btn btn-primary bg-violet-600 hover:bg-violet-700 h-10 px-4 py-2 text-white flex items-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none">
                Avanti <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" onClick={handleSave} disabled={!psychologicalProfile.trim()}
                className="btn bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white h-10 px-5 py-2 flex items-center gap-1.5 shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:pointer-events-none transform active:scale-95">
                <Check className="w-4 h-4" /> Salva e Analizza
              </button>
            )}
          </div>

          {profile && (
            <button type="button" onClick={() => setIsEditing(false)} className="absolute top-4 right-4 text-xs font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex items-center gap-1">
              Annulla
            </button>
          )}
        </div>
      ) : (
        /* ────────────────── MAIN ADVISOR VIEW ────────────────── */
        <div className="space-y-5 animate-fade-in-up">
          {/* Profile Header */}
          <div className="card p-5 bg-gradient-to-br from-violet-500/10 to-blue-500/5 border border-violet-500/20 dark:from-violet-500/20 dark:to-blue-500/5 dark:border-violet-500/30 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-none">
            <div className="flex items-start gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-violet-500/10 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 flex items-center justify-center shadow-inner flex-shrink-0">
                <Brain className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-black text-gray-900 dark:text-gray-100">Consulente AI</h3>
                  <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400">Profilazione Attiva</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400 font-bold">
                  {age !== null && <span>Età: {age} anni</span>}
                  <span className="hidden sm:inline text-gray-300 dark:text-gray-700">•</span>
                  <span>Orizzonte: {horizonLabel(profile?.timeHorizon)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap self-start md:self-auto">
              <button onClick={() => { setBirthDate(profile?.birthDate || ''); setTimeHorizon(profile?.timeHorizon || ''); setWorkType(profile?.workType || ''); setIncomeStability(profile?.incomeStability || 3); setTfrManagement(profile?.tfrManagement || ''); setRealEstate(profile?.realEstate || ''); setDebtsCredits(profile?.debtsCredits || ''); setPsychologicalProfile(profile?.psychologicalProfile || ''); setSpecialSituations(profile?.specialSituations || ''); setIsEditing(true); setCurrentStep(1); }}
                className="btn btn-secondary h-9 py-2 px-3 text-xs flex items-center gap-1.5 bg-white/70 dark:bg-gray-800/70 border-gray-200/50 hover:bg-gray-100">
                <RefreshCw className="w-3.5 h-3.5" /> Modifica Profilo
              </button>
              {aiPlan && !showCopySection && (
                <button onClick={() => setShowCopySection(true)}
                  className="btn btn-secondary h-9 py-2 px-3 text-xs flex items-center gap-1.5 bg-white/70 dark:bg-gray-800/70 border-gray-200/50 hover:bg-gray-100">
                  <RotateCcw className="w-3.5 h-3.5" /> Rigenera Prompt
                </button>
              )}
              {aiPlan && (
                <button onClick={handleCancelPlan}
                  title="Annulla Piano AI"
                  className="btn h-9 w-9 p-0 flex items-center justify-center bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200/60 dark:border-red-800/40 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl"
                  aria-label="Annulla Piano AI">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Copy/Paste section — shown when no plan yet, or when regenerating */}
          {(showCopySection || !aiPlan) && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              <div className="lg:col-span-6 card p-5 border-blue-500/20 dark:border-blue-500/30 flex flex-col justify-between space-y-4">
                <div>
                  <div className="text-[10px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-blue-500" /> Copia Prompt di Profilazione
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                    Copia il prompt con tutti i tuoi dati finanziari, storici, debiti/crediti e profilo personale. Incollalo su Gemini, ChatGPT o Claude.
                  </p>
                </div>
                <button onClick={handleCopyPrompt}
                  className={`btn h-11 w-full flex items-center justify-center gap-2 font-bold text-sm text-white transition-all transform active:scale-95 shadow-md ${copied ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20' : 'bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-750 hover:to-blue-750 shadow-violet-500/25'}`}>
                  {copied ? <><Check className="w-4 h-4" /> Prompt Copiato!</> : <><Copy className="w-4 h-4" /> Copia Prompt Finanziario</>}
                </button>
              </div>

              <div className="lg:col-span-6 card p-5 border-blue-500/20 dark:border-blue-500/30 flex flex-col justify-between space-y-3">
                <div>
                  <div className="text-[10px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-blue-500" /> Incolla il Risultato JSON
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-2">
                    Una volta ricevuta la risposta dall'AI, copia il blocco JSON ed incollalo qui sotto.
                  </p>
                  <textarea value={jsonText} onChange={(e) => { setJsonText(e.target.value); setParseError(null); }}
                    className="input h-24 py-2 font-mono text-[10px] placeholder:text-xs placeholder:font-sans resize-none"
                    placeholder="Incolla il JSON generato dall'AI..." />
                  {parseError && (
                    <p className="text-[10px] font-bold text-red-500 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {parseError}
                    </p>
                  )}
                </div>
                <button onClick={handleImportJSON} className="btn bg-gray-900 hover:bg-black text-white dark:bg-gray-100 dark:hover:bg-white dark:text-gray-950 h-10 w-full flex items-center justify-center gap-2 font-bold text-sm transition-all transform active:scale-95">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Salva ed Applica Piano
                </button>
              </div>
            </div>
          )}

          {/* ────────────────── ACTIVE AI PLAN DASHBOARD ────────────────── */}
          {aiPlan ? (
            <div className="space-y-5 animate-fade-in-up">
              {/* Plan Title and Rationale */}
              <div className="card p-6 border-violet-500/30 dark:border-violet-500/40 relative overflow-hidden bg-white/40 dark:bg-gray-900/40 backdrop-blur-md">
                <div className="absolute top-0 right-0 w-40 h-40 bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />
                <h4 className="text-xl font-black bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-500" /> {aiPlan.planName}
                </h4>
                <div className="mt-4 p-4 rounded-2xl bg-gray-50/50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800/50">
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-[9px] mb-2 flex items-center gap-1.5">
                    <Brain className="w-3.5 h-3.5 text-violet-400" /> Analisi Strategica dell'AI
                  </p>
                  <p className="text-xs text-gray-650 dark:text-gray-300 leading-relaxed italic">
                    "{aiPlan.rationale}"
                  </p>
                </div>
              </div>

              {/* Action Plan */}
              {aiPlan.actionPlan && (
                <div className="card p-5 border-amber-500/20 dark:border-amber-500/30 space-y-4">
                  <div className="text-[10px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-amber-500" /> Piano d'Azione Immediato
                  </div>

                  {/* Immediate actions */}
                  {aiPlan.actionPlan.immediateActions?.length > 0 && (
                    <div className="space-y-2">
                      {aiPlan.actionPlan.immediateActions.map((action, i) => {
                        const colorMap = {
                          vendi: { bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-200/50 dark:border-red-800/30', badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: '⬇️' },
                          mantieni: { bg: 'bg-emerald-50 dark:bg-emerald-900/10', border: 'border-emerald-200/50 dark:border-emerald-800/30', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: '✅' },
                          acquista: { bg: 'bg-blue-50 dark:bg-blue-900/10', border: 'border-blue-200/50 dark:border-blue-800/30', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: '⬆️' },
                          trasferisci: { bg: 'bg-violet-50 dark:bg-violet-900/10', border: 'border-violet-200/50 dark:border-violet-800/30', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400', icon: '↔️' },
                        };
                        const c = colorMap[action.action] || colorMap['mantieni'];
                        return (
                          <div key={i} className={`rounded-xl p-3.5 border ${c.bg} ${c.border}`}>
                            <div className="flex items-start gap-2.5">
                              <span className="text-sm mt-0.5">{c.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-extrabold uppercase ${c.badge}`}>{action.action}</span>
                                  <span className="text-xs font-black text-gray-900 dark:text-gray-100 truncate">{action.asset}</span>
                                  {action.isin && <span className="text-[10px] font-mono text-gray-400">[{action.isin}]</span>}
                                  {action.amount > 0 && <span className="text-xs font-bold text-gray-600 dark:text-gray-300 ml-auto flex-shrink-0">{formatCurrency(action.amount)}</span>}
                                </div>
                                <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">{action.reason}</p>
                                {action.capitalGainNote && (
                                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3 flex-shrink-0" /> {action.capitalGainNote}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* PAC and deposito instructions */}
                  <div className="space-y-2">
                    {aiPlan.actionPlan.pacInstructions && (
                      <div className="rounded-xl p-3.5 bg-violet-50/50 dark:bg-violet-900/10 border border-violet-200/30 dark:border-violet-800/20">
                        <p className="text-[10px] font-black text-violet-600 dark:text-violet-400 uppercase tracking-wider mb-1">📈 Piano PAC</p>
                        <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-relaxed">{aiPlan.actionPlan.pacInstructions}</p>
                      </div>
                    )}
                    {aiPlan.actionPlan.depositoInstructions && (
                      <div className="rounded-xl p-3.5 bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-200/30 dark:border-emerald-800/20">
                        <p className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">🏦 Conti Deposito</p>
                        <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-relaxed">{aiPlan.actionPlan.depositoInstructions}</p>
                      </div>
                    )}
                    {aiPlan.actionPlan.notes && (
                      <div className="rounded-xl p-3.5 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200/30 dark:border-blue-800/20">
                        <p className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1">📝 Note</p>
                        <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-relaxed">{aiPlan.actionPlan.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Targets comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Visual Allocation Targets */}
                <div className="card p-5 space-y-4">
                  <div className="text-[10px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-violet-500" /> Allocazione Target AI vs Attuale
                  </div>
                  <div className="space-y-4">
                    {rebalancingAdvice?.map((item) => {
                      const delta = item.target - item.actual;
                      return (
                        <div key={item.name} className="space-y-1">
                          <div className="flex justify-between text-xs font-bold">
                            <span className="text-gray-800 dark:text-gray-200">{item.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-gray-400">Attuale: {item.actual.toFixed(1)}%</span>
                              <span className="text-gray-900 dark:text-gray-100">Target: {item.target}%</span>
                            </div>
                          </div>
                          <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden relative">
                            <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10" style={{ left: `${item.target}%` }} title={`Target: ${item.target}%`} />
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${item.actual}%`, backgroundColor: item.color }} />
                          </div>
                          <div className="flex justify-between items-center text-[10px] font-bold">
                            <span className="text-gray-400">
                              {totalInvested > 0 ? formatCurrency((item.target / 100) * totalInvested) : '€0'} consigliati
                            </span>
                            <span className={delta > 2 ? 'text-emerald-500' : delta < -2 ? 'text-amber-500' : 'text-gray-400'}>
                              {delta > 2 ? `Sotto-esposto (+${delta.toFixed(1)}%)` : delta < -2 ? `Sovra-esposto (${delta.toFixed(1)}%)` : 'In linea ✓'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* PAC & Emergency Fund */}
                <div className="card p-5 flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="text-[10px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-violet-500" /> Coperture & Risparmio Consigliati
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl p-3 bg-gradient-to-br from-violet-500/10 to-violet-600/5 border border-violet-500/20 dark:border-violet-500/30">
                        <div className="text-[9px] uppercase font-bold text-violet-600 dark:text-violet-400 mb-0.5">PAC Mensile AI</div>
                        <div className="text-base font-black text-violet-700 dark:text-violet-300">{formatCurrency(aiPlan.recommendedPac)}</div>
                        <div className="text-[9px] text-gray-400 leading-tight mt-1">Attuale: {formatCurrency(pacAttuale)}/mese</div>
                      </div>
                      <div className="rounded-xl p-3 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 dark:border-emerald-500/30">
                        <div className="text-[9px] uppercase font-bold text-emerald-600 dark:text-emerald-400 mb-0.5">Fondo Emergenza AI</div>
                        <div className="text-base font-black text-emerald-700 dark:text-emerald-300">{formatCurrency(aiPlan.recommendedEmergencyFund)}</div>
                        <div className="text-[9px] text-gray-400 leading-tight mt-1">Liquidità + Deposito: {formatCurrency(totalLiquidity)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Embedded Timeline Simulation */}
              {embeddedTimelineData.length > 1 && (
                <div className="card p-5 space-y-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[10px] uppercase font-black tracking-widest text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-amber-500" /> Simulazione Timeline Patrimoniale ({aiPlan.simulationTimeline?.projYears || 10} anni)
                    </div>
                    {finalTimelineValue && (
                      <div className="text-right">
                        <div className="text-[9px] text-gray-400 uppercase font-bold">Valore stimato finale</div>
                        <div className="text-sm font-black text-amber-600 dark:text-amber-400">{fmtShort(finalTimelineValue.total)}</div>
                      </div>
                    )}
                  </div>

                  <div style={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={embeddedTimelineData} margin={{ top: 5, right: 5, left: 8, bottom: 0 }}>
                        <defs>
                          <linearGradient id="aiCapGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/><stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="aiIntGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/><stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.15} />
                        <XAxis dataKey="year" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={42} />
                        <Tooltip content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload;
                          return (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-2.5 text-xs min-w-[160px] space-y-1">
                              <p className="font-bold text-gray-400 uppercase text-[10px]">{d?.year}</p>
                              <div className="flex justify-between gap-3"><span className="text-indigo-500">Capitale:</span><span className="font-bold">{fmtShort(d?.capitale)}</span></div>
                              <div className="flex justify-between gap-3"><span className="text-amber-500">Rendimento:</span><span className="font-bold">{fmtShort(d?.interessi)}</span></div>
                              <div className="border-t border-gray-100 dark:border-gray-700 pt-1 flex justify-between gap-3 font-black">
                                <span>Totale:</span><span className="text-amber-500">{fmtShort(d?.total)}</span>
                              </div>
                            </div>
                          );
                        }} />
                        <Area type="monotone" dataKey="capitale" name="Capitale" stackId="1" stroke="#6366f1" fill="url(#aiCapGrad)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="interessi" name="Rendimento" stackId="1" stroke="#f59e0b" fill="url(#aiIntGrad)" strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center gap-4 justify-center flex-wrap">
                    <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" /> Capitale</span>
                    <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Rendimento</span>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200/30 dark:border-blue-800/20 text-[11px] text-blue-800 dark:text-blue-300 leading-relaxed">
                    <p className="font-bold flex items-center gap-1 mb-1"><Info className="w-3.5 h-3.5 text-blue-500" /> Simulazione completa disponibile</p>
                    Questa è una preview. La simulazione completa con parametri modificabili è nella scheda <em>Simulazione Timeline</em>.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-10 text-center card bg-gray-50/50 dark:bg-gray-900/30 border-dashed border-2 border-gray-250 dark:border-gray-800 flex flex-col items-center justify-center space-y-2">
              <Sparkles className="w-8 h-8 text-gray-300 dark:text-gray-600 animate-pulse" />
              <h5 className="text-sm font-bold text-gray-700 dark:text-gray-300">Nessun piano finanziario AI attivo</h5>
              <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm">
                Copia il prompt finanziario sopra, incollalo su ChatGPT o Gemini, e incolla la risposta JSON a destra per sbloccare la tua dashboard premium.
              </p>
            </div>
          )}
        </div>
      )}

      {showSuccessToast && (
        <div className="fixed bottom-5 right-5 z-50 max-w-sm rounded-2xl bg-emerald-50/95 dark:bg-gray-900/95 backdrop-blur-md border border-emerald-200/50 dark:border-emerald-800/40 p-4 shadow-xl shadow-emerald-500/10 dark:shadow-emerald-500/2 flex items-start gap-3 animate-fade-in-up">
          <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0 shadow-sm animate-pulse">
            <CheckCircle2 className="w-4.5 h-4.5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-xs font-black text-gray-900 dark:text-gray-100">Piano AI Importato!</h4>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">Il tuo piano finanziario è stato configurato ed applicato con successo.</p>
          </div>
          <button onClick={() => setShowSuccessToast(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs font-bold font-mono p-1">
            ×
          </button>
        </div>
      )}
    </div>
  );
}
